import React, { useEffect } from 'react'
import { useController, Control } from 'react-hook-form'
import { Label, Input, HiddenCheckbox, StyledCheckbox } from '../styles/modal-style'
import CurrencyInput from '../CurrencyInput'
import InfiniteSelect from '../InfiniteSelect'
import axios from '../../config/axios'
import { getModuleById } from './Modals/integrationModulesConfig'

export interface IntegrationConfig {
  moduleId: string
  moduleName: string
  endpoint: string
  createEndpoint?: string
  fieldKey: string
  fieldName: string
  integrationType: 'read' | 'write'
  isRequired?: boolean
  responseKey?: string
  paginationType?: 'offset' | 'page' | 'skip-take'
}

export interface FieldInTicket {
  ticketId?: number
  pipelineFieldId: number
  stringValue?: string
  bigStringValue?: string
  integerValue?: number
  decimalValue?: number
  dateTimeValue?: string
  booleanValue?: boolean
  pipelineField: {
    id: number
    name: string
    typeFieldId: number
    typeField?: {
      id: number
      name: string
    }
    integrationConfig?: IntegrationConfig
  }
  isRequired: boolean
}

export interface Ticket {
  id: number
  title: string
  deadline: Date
  urgency: boolean
  description: string
  stepId: number
  fieldInTickets: FieldInTicket[]
}

interface PropsInterface {
  field: FieldInTicket
  setValue: React.Dispatch<React.SetStateAction<Ticket>>
  isFilled: boolean
  control?: Control<any>
}

const RenderField:React.FC<PropsInterface> = ({field, setValue, isFilled}) => {
  const typeId = field.pipelineField.typeFieldId
  const typeName = field.pipelineField.typeField?.name || ''
  const integrationConfig = field.pipelineField.integrationConfig
  const isIntegrated = !!integrationConfig
  
  const [sampleData, setSampleData] = React.useState<any>(null)
  const sampleDataRef = React.useRef<any>(null)
  const [searchTerm, setSearchTerm] = React.useState<string>('')
  
  const getNestedValue = React.useCallback((obj: any, path: string): any => {
    const keys = path.split('.')
    let val = obj
    for (const k of keys) {
      if (val === null || val === undefined) {
        return null
      }
      val = val[k]
    }
    return val
  }, [])
  
  const hasValidFieldValue = React.useCallback((item: any, fieldKey: string): boolean => {
    const value = getNestedValue(item, fieldKey)
    
    if (value === null || value === undefined) {
      return false
    }
    if (value === '') {
      return false
    }
    if (Array.isArray(value) && value.length === 0) {
      return false
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
      const objName = value.name || value.typeName || value.companyName || value.id
      return objName !== null && objName !== undefined && objName !== ''
    }
    return true
  }, [getNestedValue])

  const hasValidScalarValue = React.useCallback((item: any, fieldKey: string): boolean => {
    const value = getNestedValue(item, fieldKey)
    if (value === null || value === undefined) return false
    if (value === '') return false
    if (typeof value === 'number' && value === 0) return false
    if (typeof value === 'object' && !Array.isArray(value)) {
      const objName = value.name || value.typeName || value.companyName || value.id
      return objName !== null && objName !== undefined && objName !== ''
    }
    return true
  }, [getNestedValue])
  
  const extractDisplayName = React.useCallback((v: any): string => {
    if (typeof v !== 'object' || v === null) return String(v ?? '')
    if (v.name) return v.name
    if (v.typeName) return v.typeName
    if (v.companyName) return v.companyName
    const nestedKeys = ['Grade', 'Group', 'Material', 'CollectionColor', 'ItemColor', 'Stage', 'Resource', 'Stock']
    for (const key of nestedKeys) {
      if (v[key] && typeof v[key] === 'object') {
        const inner = v[key]
        if (inner.name) return inner.name
        if (inner.description) return inner.description
      }
    }
    return String(v.id ?? '')
  }, [])

  const getDisplayValueFromField = React.useCallback((item: any, fieldKey: string): string => {
    const value = getNestedValue(item, fieldKey)
    
    if (value === null || value === undefined) return ''
    if (Array.isArray(value)) {
      const names = value.map(v => extractDisplayName(v)).filter(Boolean)
      if (names.length === 0) return ''
      if (names.length <= 3) return names.join(', ')
      return `${names.slice(0, 3).join(', ')} +${names.length - 3}`
    }
    if (typeof value === 'object') {
      return value.name || value.typeName || value.companyName || String(value.id || '')
    }
    return String(value)
  }, [getNestedValue, extractDisplayName])
  
  useEffect(() => {
    const fetchIntegrationDataOnMount = async () => {
      if (!isIntegrated || !integrationConfig || !field.integerValue || (field as any).integrationData) return
      
      try {
        const moduleConfig = getModuleById(integrationConfig.moduleId)
        const resolvedEndpoint = moduleConfig?.endpoint || integrationConfig.endpoint
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}${resolvedEndpoint}/${field.integerValue}`
        )
        const data = response.data?.data || response.data
        
        if (data) {
          setSampleData(data)
          setValue(prev => ({
            ...prev,
            fieldInTickets: prev.fieldInTickets.map(f => f.pipelineFieldId === field.pipelineFieldId ? {
              ...f,
              integrationData: data
            } : f)
          }))
        }
      } catch (error) {
        console.error('Erro ao buscar dados integrados:', error)
      }
    }
    
    fetchIntegrationDataOnMount()
  }, [field.integerValue, isIntegrated])
  
  const isEmail = typeName === 'E-mail'
  const isCpf = typeName === 'CPF'
  const isCnpj = typeName === 'CNPJ'
  const isDate = typeName === 'Data'
  const isCheckbox = typeName === 'Checkbox'
  const isCelular = typeName === 'Celular'
  const isTelefone = typeName === 'Telefone'
  const isTexto = typeName === 'Texto simples'
  const isValor = typeName === 'Valor (R$)'
  const isString = isEmail || isCpf || isCnpj || isCelular || isTelefone || isTexto

  const maskCpf = (v: string) => v.replace(/\D/g, '').slice(0,11).replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2')
  const maskCnpj = (v: string) => v.replace(/\D/g, '').slice(0,14).replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d{1,2})$/, '$1-$2')
  const maskPhone = (v: string) => {
    const digits = v.replace(/\D/g, '')
    if (isCelular) {
      const sliced = digits.slice(0,11)
      if (sliced.length <= 10) return sliced.replace(/(\d{2})(\d{0,4})(\d{0,4})/, (_, a, b, c) => c ? `(${a}) ${b}-${c}` : b ? `(${a}) ${b}` : `(${a}`)
      return sliced.replace(/(\d{2})(\d{5})(\d{0,4})/, (_, a, b, c) => c ? `(${a}) ${b}-${c}` : `(${a}) ${b}`)
    }
    const sliced = digits.slice(0,10)
    return sliced.replace(/(\d{2})(\d{0,4})(\d{0,4})/, (_, a, b, c) => c ? `(${a}) ${b}-${c}` : b ? `(${a}) ${b}` : `(${a}`)
  }

  const placeholder = isTexto ? 'Digite aqui' : 'Digite...'

  const handleStringChange = (raw: string) => {
    let formatted = raw
    if (isCpf) formatted = maskCpf(raw)
    if (isCnpj) formatted = maskCnpj(raw)
    if (isCelular || isTelefone) formatted = maskPhone(raw)

    setValue(prev => ({
      ...prev,
      fieldInTickets: prev.fieldInTickets.map(f => f.pipelineFieldId === field.pipelineFieldId ? {
        ...f,
        stringValue: formatted
      } : f)
    }))
  }

  const handleDecimalChange = (_value: string | undefined, _name?: string, values?: any) => {
    const floatValue = values?.float
    setValue(prev => ({
      ...prev,
      fieldInTickets: prev.fieldInTickets.map(f => f.pipelineFieldId === field.pipelineFieldId ? {
        ...f,
        decimalValue: floatValue
      } : f)
    }))
  }

  const handleDateChange = (value: string) => {
    setValue(prev => ({
      ...prev,
      fieldInTickets: prev.fieldInTickets.map(f => f.pipelineFieldId === field.pipelineFieldId ? {
        ...f,
        dateTimeValue: value
      } : f)
    }))
  }

  const handleToggle = () => {
    setValue(prev => ({
      ...prev,
      fieldInTickets: prev.fieldInTickets.map(f => f.pipelineFieldId === field.pipelineFieldId ? {
        ...f,
        booleanValue: !f.booleanValue
      } : f)
    }))
  }

  const handleIntegrationSelect = (option: any | null) => {
    console.log('[RenderField] handleIntegrationSelect chamado → option:', option)
    const fieldKey = integrationConfig?.fieldKey || 'name'
    const displayValue = option ? (option._displayName || getDisplayValueFromField(option, fieldKey) || getPrimaryLabel(option)) : ''
    console.log('[RenderField] handleIntegrationSelect → fieldKey:', fieldKey, 'displayValue:', displayValue, 'integerValue:', option?.id)
    
    setValue(prev => ({
      ...prev,
      fieldInTickets: prev.fieldInTickets.map(f => f.pipelineFieldId === field.pipelineFieldId ? {
        ...f,
        integerValue: option?.id || null,
        stringValue: displayValue,
        integrationData: option || null
      } : f)
    }))
  }

  const getPrimaryLabel = React.useCallback((option: any): string => {
    if (!option) return ''
    const label = option.name || option.description || option.companyName || option.productCode || option.codeOfOP || option.plate || ''
    console.log('[getPrimaryLabel] option.id:', option?.id, 'label:', label || String(option.id || ''), 'option:', option)
    return label || String(option.id || '')
  }, [])

  const fetchIntegrationData = React.useCallback(async (limit: number, offset: number, search?: string) => {
    if (!integrationConfig) {
      return []
    }
    
    const moduleConfig = getModuleById(integrationConfig.moduleId)
    const fieldKey = integrationConfig.fieldKey
    const url = `${process.env.REACT_APP_GATEWAY_BASE_URL}${moduleConfig?.endpoint || integrationConfig.endpoint}`
    const paginationType = moduleConfig?.paginationType || (integrationConfig as any).paginationType || 'offset'
    const responseKey = moduleConfig?.responseKey ?? (integrationConfig as any).responseKey

    console.log('[fetchIntegrationData] moduleId:', integrationConfig.moduleId, 'fieldKey:', fieldKey, 'url:', url, 'paginationType:', paginationType, 'responseKey:', responseKey)

    let params: any
    if (search && search.trim()) {
      params = paginationType === 'page'
        ? { limit: 100, page: 1, search }
        : paginationType === 'skip-take'
        ? { skip: 0, take: 100, search }
        : { limit: 100, offset: 0, search }
    } else {
      params = paginationType === 'page'
        ? { limit, page: Math.floor(offset / limit) + 1 }
        : paginationType === 'skip-take'
        ? { skip: offset, take: limit }
        : { limit, offset }
    }
    
    try {
      const response = await axios.get(url, { params })
      const raw = response.data
      const extracted = (() => {
        if (responseKey) {
          const keyed = raw?.[responseKey]
          if (Array.isArray(keyed)) return keyed
        }
        if (Array.isArray(raw)) return raw
        if (Array.isArray(raw?.data)) return raw.data
        if (Array.isArray(raw?.content)) return raw.content
        if (Array.isArray(raw?.items)) return raw.items
        if (Array.isArray(raw?.results)) return raw.results
        if (raw && typeof raw === 'object') {
          const firstArrayKey = Object.keys(raw).find(k => Array.isArray(raw[k]))
          if (firstArrayKey) return raw[firstArrayKey]
        }
        return raw
      })()
      const data = Array.isArray(extracted) ? extracted : []
      
      console.log('[fetchIntegrationData] primeiro registro bruto da API:', data[0])
      
      const sampleFieldValue = data.length > 0 ? getNestedValue(data[0], fieldKey) : undefined
      const isArrayField = Array.isArray(sampleFieldValue)
      const fieldConfig = moduleConfig?.fields?.find(f => f.field === fieldKey)

      let resultData: any[]
      if (isArrayField && fieldConfig?.filterEmpty) {

        const seenIds = new Set<any>()
        const expanded: any[] = []
        for (const record of data) {
          const arr = getNestedValue(record, fieldKey)
          if (!Array.isArray(arr) || arr.length === 0) continue
          for (const element of arr) {
            const elemId = element?.id
            if (elemId !== undefined && seenIds.has(elemId)) continue
            if (elemId !== undefined) seenIds.add(elemId)
            const displayName = extractDisplayName(element)
            if (!displayName) continue
            expanded.push({ ...element, _parentRecord: record, _displayName: displayName })
          }
        }
        resultData = expanded
      } else if (isArrayField) {
        resultData = data
      } else {
        resultData = data.filter((item: any) => hasValidScalarValue(item, fieldKey))
      }

      console.log('[fetchIntegrationData] fieldKey:', fieldKey, 'total:', data.length, 'isArrayField:', isArrayField, 'filterEmpty:', fieldConfig?.filterEmpty, 'após expansão/filtro:', resultData.length)

      if (resultData.length > 0 && !sampleDataRef.current) {
        sampleDataRef.current = resultData[0]
        setSampleData(resultData[0])
      }

      if (search && search.trim()) {
        const searchLower = search.toLowerCase().trim()
        return resultData.filter((item: any) => {
          const primaryLabel = item._displayName || getPrimaryLabel(item)
          const fieldValue = getDisplayValueFromField(item, fieldKey)
          return primaryLabel.toLowerCase().includes(searchLower) ||
                 fieldValue.toLowerCase().includes(searchLower) ||
                 String(item.id || '').includes(searchLower)
        })
      }

      return resultData
    } catch (error) {
      return []
    }
  }, [integrationConfig, getNestedValue, getDisplayValueFromField, getPrimaryLabel, hasValidScalarValue])

  const getIntegrationDisplayValue = () => {
    if (!field.integerValue) return null
    if (field.stringValue) {
      return { id: field.integerValue, name: field.stringValue }
    }
    const cached = (field as any).integrationData || sampleDataRef.current
    if (cached) {
      const fieldKey = integrationConfig?.fieldKey || 'name'
      const displayValue = getDisplayValueFromField(cached, fieldKey)
      return { id: field.integerValue, name: displayValue || cached.name || cached.companyName || String(field.integerValue) }
    }
    return { id: field.integerValue, name: String(field.integerValue) }
  }
  
  const translateFieldLabel = (key: string): string => {
    const translations: Record<string, string> = {
      'id': 'ID',
      'name': 'Nome',
      'email': 'E-mail',
      'password': 'Senha',
      'cnpj': 'CNPJ',
      'company': 'Empresa',
      'companyName': 'Razão Social',
      'phone': 'Telefone',
      'adminUser': 'Usuário Admin',
      'picture': 'Foto',
      'approved': 'Aprovado',
      'role': 'Papel',
      'tenantId': 'ID do Tenant',
      'createdAt': 'Criado em',
      'updatedAt': 'Atualizado em',
      'updateAt': 'Atualizado em',
      'expiresAt': 'Expira em',
      'token': 'Token',
      'resetToken': 'Token de Reset',
      'resetTokenExpiresAt': 'Token expira em',
      'code': 'Código',
      'density': 'Densidade',
      'stateRegistration': 'Inscrição Estadual',
      'contactName': 'Nome do Contato',
      'activedDays': 'Dias Ativos',
      'startTime': 'Início',
      'endTime': 'Término',
      'addressId': 'ID do Endereço',
      'cpf': 'CPF',
      'isMain': 'Principal',
      'typeName': 'Nome do Tipo',
      'status': 'Status',
      'productionPace': 'Ritmo de Produção',
      'productionDay': 'Dia de Produção',
      'codeOfOP': 'Código da OP',
      'integratedWithOmie': 'Integrado com Omie',
      'usedQuantity': 'Qtd Usada',
      'usedUnitOfMeasurement': 'Unid Medida Usada',
      'action': 'Ação',
      'dateTime': 'Data e Hora',
      'description': 'Descrição',
      'newValue': 'Novo Valor',
      'oldValue': 'Antigo Valor',
      'completed': 'Concluído',
      'observation': 'Observação',
      'index': 'Índice',
      'fileUrl': 'URL do Arquivo',
      'executeDate': 'Data de Execução',
      'completedAt': 'Concluído em',
      'startedAt': 'Iniciado em',
      'uf': 'UF',
      'cep': 'CEP',
      'street': 'Rua',
      'complement': 'Complemento',
      'number': 'Número',
      'attachments': 'Anexos',
      'deliveryDate': 'Data de Entrega',
      'extraInfo': 'Info Extra',
      'orderDate': 'Data do Pedido',
      'orderType': 'Tipo do Pedido',
      'paymentTerm': 'Prazo Pagamento',
      'shippingType': 'Tipo de Frete',
      'shippingValue': 'Valor do Frete',
      'taxes': 'Impostos',
      'total': 'Total',
      'trackingCode': 'Código de Rastreio',
      'totalQuantity': 'Qtd Total',
      'originDealIdPloomes': 'ID Negócio Ploomes',
      'saleNumberPloomes': 'Nº Venda Ploomes',
      'isPrincipal': 'Principal',
      'descount': 'Desconto',
      'priceKg': 'Preço/Kg',
      'priceKgMin': 'Preço/Kg Mín',
      'time': 'Tempo',
      'quantity': 'Quantidade',
      'diameter': 'Diâmetro',
      'length': 'Comprimento',
      'ploomesProductName': 'Produto Ploomes',
      'thickness': 'Espessura',
      'weight': 'Peso',
      'width': 'Largura',
      'requestedProduct': 'Produto Solicitado',
      'offeredProduct': 'Produto Oferecido',
      'deadlines': 'Prazos',
      'heatTreatments': 'Tratamentos Térmicos',
      'segment': 'Segmento',
      'price': 'Preço',
      'cost': 'Custo',
      'stock': 'Estoque',
      'category': 'Categoria',
      'type': 'Tipo',
      'unit': 'Unidade',
      'model': 'Modelo',
      'costPerHour': 'Custo/Hora',
      'capacity': 'Capacidade',
      'dimension': 'Dimensão',
      'manufacturer': 'Fabricante',
      'year': 'Ano',
      'locationId': 'ID da Localização',
      'typeId': 'ID do Tipo',
      'deadlineInDays': 'Prazo (Dias)',
      'lengthInMm': 'Comprim. (mm)',
      'stockUnit': 'Unid. Estoque',
      'totalWeight': 'Peso Total',
      'unitQuantity': 'Qtd Unitária'
    }
    
    return translations[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')
  }
  
  const getIntegrationFieldsConfig = () => {
    if (!integrationConfig) return []
    
    const data = sampleData || (field as any).integrationData
    if (!data) return []
    
    const fieldKey = integrationConfig.fieldKey
    const excludeKeys = ['id', fieldKey, 'tenantId', 'createdAt', 'updatedAt', 'updateAt', 'deletedAt']
    
    const fields: any[] = []


    if (data._parentRecord) {
      const parentLabel = getPrimaryLabel(data._parentRecord)
      if (parentLabel) {
        fields.push({
          key: '_parent',
          label: 'Registro',
          render: () => parentLabel,
          condition: (option: any) => !!option._parentRecord,
          getValue: (option: any) => option._parentRecord ? getPrimaryLabel(option._parentRecord) : null
        })
      }
    }
    
    const processObject = (obj: any, prefix = '') => {
      Object.keys(obj).forEach(key => {
        const fullKey = prefix ? `${prefix}.${key}` : key
        const value = obj[key]
        
        if (key.startsWith('_') || excludeKeys.includes(key) || key.toLowerCase().includes('id')) return
        
        if (Array.isArray(value)) {
          if (value.length > 0) {
            fields.push({
              key: fullKey,
              label: translateFieldLabel(key),
              render: (val: any) => {
                if (!Array.isArray(val) || val.length === 0) return ''
                const names = val.map((v: any) => extractDisplayName(v)).filter(Boolean)
                if (names.length <= 3) return names.join(', ')
                return `${names.slice(0, 3).join(', ')} +${names.length - 3}`
              },
              condition: (option: any) => {
                const keys = fullKey.split('.')
                let val: any = option
                for (const k of keys) val = val?.[k]
                return Array.isArray(val) && val.length > 0
              },
              getValue: (option: any) => {
                const keys = fullKey.split('.')
                let val: any = option
                for (const k of keys) val = val?.[k]
                return val
              }
            })
          }
        } else if (value && typeof value === 'object') {
          processObject(value, fullKey)
        } else if (value !== null && value !== undefined && value !== '') {
          fields.push({
            key: fullKey,
            label: translateFieldLabel(key),
            render: (val: any) => {
              const displayVal = typeof val === 'object' ? val?.name || val?.companyName || val?.id || String(val) : String(val)
              
              if (key.toLowerCase() === 'cnpj') return maskCnpj(displayVal)
              if (key.toLowerCase() === 'cpf') return maskCpf(displayVal)
              if (key.toLowerCase().includes('phone') || key.toLowerCase().includes('telefone') || key.toLowerCase().includes('celular')) {
                const digits = displayVal.replace(/\D/g, '')
                if (digits.length === 11) return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
                if (digits.length === 10) return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
              }
              return displayVal
            },
            condition: (option: any) => {
              const keys = fullKey.split('.')
              let val: any = option
              for (const k of keys) {
                val = val?.[k]
              }
              return val !== null && val !== undefined && val !== ''
            },
            getValue: (option: any) => {
              const keys = fullKey.split('.')
              let val: any = option
              for (const k of keys) {
                val = val?.[k]
              }
              return val
            }
          })
        }
      })
    }
    
    processObject(data)
    
    return fields.slice(0, 5)
  }
  return (
    <>
      <Label>{field.pipelineField.name}</Label>
      {isIntegrated && integrationConfig ? (
        <InfiniteSelect
          placeholder='Selecione...'
          value={getIntegrationDisplayValue()}
          onSelect={handleIntegrationSelect}
          fetchFunction={fetchIntegrationData}
          mode={integrationConfig.integrationType === 'write' ? 'crud' : 'readonly'}
          allowCreate={integrationConfig.integrationType === 'write'}
          allowDelete={false}
          getOptionLabel={(option) => {
            const fieldKey = integrationConfig.fieldKey

            if (option._parentRecord !== undefined) {
              return option._displayName || getPrimaryLabel(option)
            }
            const colValue = getDisplayValueFromField(option, fieldKey)
            if (colValue) {
              console.log('[getOptionLabel] moduleId:', integrationConfig.moduleId, 'fieldKey:', fieldKey, 'colValue:', colValue, 'id:', option?.id)
              return colValue
            }
            const fallback = getPrimaryLabel(option)
            console.log('[getOptionLabel] fallback moduleId:', integrationConfig.moduleId, 'fieldKey:', fieldKey, 'fallback:', fallback, 'id:', option?.id)
            return fallback
          }}
          fieldsConfig={getIntegrationFieldsConfig()}
          showDefaultFields={true}
          endpointConfig={integrationConfig.integrationType === 'write' ? {
            listPath: integrationConfig.endpoint,
            createPath: integrationConfig.createEndpoint || integrationConfig.endpoint
          } : undefined}
        />
      ) : isCheckbox ? (
        <>
          <HiddenCheckbox checked={field.booleanValue || false} onChange={handleToggle} />
          <StyledCheckbox checked={field.booleanValue || false} onClick={handleToggle} />
        </>
      ) : isValor ? (
        <CurrencyInput
          id={`currency-${field.pipelineFieldId}`}
          name={`currency-${field.pipelineFieldId}`}
          value={field.decimalValue || 0}
          onValueChange={handleDecimalChange}
          placeholder='R$ 0,00'
        />
      ) : isDate ? (
        <Input
          isFilled={isFilled === false && field.isRequired === true ? false : undefined}
          type='date'
          placeholder='Selecione...'
          value={field.dateTimeValue ? field.dateTimeValue.split('T')[0] : ''}
          onChange={(e) => handleDateChange(e.target.value)}
        />
      ) : (
        <Input
          isFilled={isFilled === false && field.isRequired === true ? false : undefined}
          type={isEmail ? 'email' : 'text'}
          placeholder={placeholder}
          value={field.stringValue || ''}
          onChange={(e) => handleStringChange(e.target.value)}
        />
      )}
    </>
  )
}

export default RenderField